// Lightweight, lazy Sentry bridges to keep Sentry SDK out of initial bundles.
import { LoggedError } from './logged-error';

export async function initializeSentryLazy(): Promise<void> {
  try {
    const mod = await import('./sentry-config');
    await mod.initializeSentry();
  } catch {
    // no-op
  }
}

export function captureErrorLazy(error: Error, context?: {
  component?: string;
  feature?: string;
  user_action?: string;
  extra?: Record<string, unknown>;
}): void {
  import('./sentry-config')
    .then((m) => m.captureError(error, context))
    .catch(() => void 0);
}

export function captureMessageLazy(
  message: string,
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info',
  context?: Record<string, unknown>
): void {
  import('./sentry-config')
    .then((m) => m.captureMessage(message, level, context))
    .catch(() => void 0);
}

export function setSentryErrorBoundaryComponentLazy(componentName: string): void {
  import('./sentry-config')
    .then((m) => m.setSentryErrorBoundaryComponent(componentName))
    .catch(() => void 0);
}

export const loggerLazy = {
  debug: (message: string, extra?: Record<string, unknown>) => import('./sentry-config').then((m) => m.sentryLogger.debug(message, extra)).catch(() => void 0),
  info: (message: string, extra?: Record<string, unknown>) => import('./sentry-config').then((m) => m.sentryLogger.info(message, extra)).catch(() => void 0),
  warn: (message: string, extra?: Record<string, unknown>) => import('./sentry-config').then((m) => m.sentryLogger.warn(message, extra)).catch(() => void 0),
  // error/fatal: the Error (or a LoggedError) is made HERE, synchronously, so
  // its stack points at the code that logged rather than at this async bridge.
  error: (message: string, extra?: Record<string, unknown>, error?: Error) => {
    const err = error ?? new LoggedError(message);
    return import('./sentry-config').then((m) => m.sentryLogger.error(message, extra, err)).catch(() => void 0);
  },
  trace: (message: string, extra?: Record<string, unknown>) => import('./sentry-config').then((m) => m.sentryLogger.trace(message, extra)).catch(() => void 0),
  fatal: (message: string, extra?: Record<string, unknown>, error?: Error) => {
    const err = error ?? new LoggedError(message);
    return import('./sentry-config').then((m) => m.sentryLogger.fatal(message, extra, err)).catch(() => void 0);
  },
  fmt: (template: TemplateStringsArray, ...values: unknown[]) => import('./sentry-config').then((m) => m.sentryLogger.fmt(template, ...values)).catch(() => ''),
};

// Best-effort span wrapper. If Sentry is loaded, it will use startSpan; otherwise runs fn immediately.
export function withSpan<T>(opts: { op?: string; name: string }, fn: () => T): T {
  let returned: T;
  try {
    returned = fn();
  } finally {
    // Attempt to record the span asynchronously without blocking the call.
    import('./sentry-config')
      .then(({ Sentry }) => {
        try {
          Sentry.startSpan({ op: opts.op || 'function', name: opts.name }, () => void 0);
        } catch {
          // ignore
        }
      })
      .catch(() => void 0);
  }
  return returned;
}


